import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import EmailAddress from "_shared/email-addresses/email-address";
import EmailAddressSchema from "_shared/email-addresses/email-address-schema";
import Guid from "_shared/guids/guid";
import GuidSchema from "_shared/guids/guid-schema";
import Name from "_shared/names/name";
import NameSchema from "_shared/names/name-schema";
import { z } from "zod";

export type UserJSON = z.infer<typeof UserSchema.UserJSONZ>;
export type UserParams = z.infer<typeof UserSchema.UserZ>;

export default class UserSchema {
  public static readonly UserJSONZ = z.object({
    id: z.uuid().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    email: z.email().nullish(),
    created: z.iso.datetime({ offset: true }).optional(),
    isSuperUser: z.boolean().optional(),
    userIsDisabled: z.boolean().optional(),
    individualId: z.uuid().optional(),
    companyName: z.string().min(1).nullish(),
    legalEntityId: z.uuid(),
    isCompanyManager: z.boolean().optional(),
    companyId: z.uuid().optional(),
  });

  public static readonly UserZ = z.object({
    id: z.instanceof(Guid).optional(),
    name: z.instanceof(Name),
    email: z.instanceof(EmailAddress).optional(),
    created: z.instanceof(AHDate).optional(),
    isSuperUser: z.boolean().optional(),
    userIsDisabled: z.boolean().optional(),
    individualId: z.instanceof(Guid).optional(),
    companyName: z.string().nullish(),
    legalEntityId: z.instanceof(Guid),
    hasSigningAuthority: z.boolean().optional(),
    companyId: z.instanceof(Guid).optional(),
  });

  public static validate(values: unknown): UserParams {
    return parseOrThrow(UserSchema.UserZ, values, {
      logError: true,
      schemaName: "UserSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): UserJSON {
    return parseOrThrow(UserSchema.UserJSONZ, json, {
      logError: true,
      schemaName: "UserSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is UserParams {
    try {
      UserSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is UserJSON {
    try {
      UserSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: unknown): UserParams {
    const parsed = parseOrThrow(UserSchema.UserJSONZ, json, {
      logError: true,
      schemaName: "UserSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return UserSchema.codec.decode(parsed);
  }

  public static serialize(legalEntity: UserParams): UserJSON {
    if (isNullOrUndefined(legalEntity)) return legalEntity;
    const parsed = UserSchema.validate(legalEntity);
    if (isNullOrUndefined(parsed)) return parsed;
    return UserSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(UserSchema.UserJSONZ, UserSchema.UserZ, {
    decode: (data) => ({
      id: Guid.fromJSON(data.id) ?? undefined,
      name: new Name(NameSchema.deserialize({ firstName: data.firstName, lastName: data.lastName })),
      email: new EmailAddress(EmailAddressSchema.deserialize(data.email)),
      created: AHDate.fromJSON(data.created) ?? undefined,
      isSuperUser: data.isSuperUser,
      userIsDisabled: data.userIsDisabled,
      individualId: Guid.fromJSON(data.individualId) ?? undefined,
      companyName: data.companyName,
      legalEntityId: new Guid(GuidSchema.deserialize(data.legalEntityId)),
      hasSigningAuthority: data.isCompanyManager,
      companyId: Guid.fromJSON(data.companyId) ?? undefined,
    }),
    encode: (entity) => ({
      id: entity.id?.toJSON(),
      firstName: entity.name.firstName,
      lastName: entity.name.lastName,
      email: entity.email?.toJSON(),
      created: entity.created?.toJSON(),
      isSuperUser: entity.isSuperUser,
      userIsDisabled: entity.userIsDisabled,
      individualId: entity.individualId?.toJSON(),
      companyName: entity.companyName,
      legalEntityId: entity.legalEntityId?.toJSON(),
      isCompanyManager: entity.hasSigningAuthority,
      companyId: entity.companyId?.toJSON(),
    }),
  });
}
